"use client";

import {
  VStack,
  Flex,
  Image,
  Divider,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Button,
  Alert,
  AlertIcon,
  CloseButton,
  Text,
  Link,
} from "@chakra-ui/react";

import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faLock,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";

const MotionVStack = motion(VStack);
const MotionAlert = motion(Alert);

const LoginForm = ({
  username,
  setUsername,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  isLoading,
  error,
  setError,
  userNotFound,
  handleLogin,
  handleRegister,

  // nuevos props:
  isRecovering,
  setIsRecovering,
  recoverEmail,
  setRecoverEmail,
  handlePasswordReset,
  recoverySuccess,
  setRecoverySuccess,
  recoveryError,
  setRecoveryError,
}) => {
  const fadeVariants = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  return (
    <MotionVStack
      key="login"
      variants={fadeVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      flex="1"
      w="100%"
      justifyContent="space-between"
      align="stretch"
    >
      <Flex justifyContent="center">
        <Image
          display={{ base: "block", lg: "block" }}
          src="assets/logo2025_dark.png"
          maxW="60%"
        />
      </Flex>
      <Divider display={{ base: "block", lg: "block" }} />

      {/* FORMULARIO DE RECUPERACIÓN */}
      {isRecovering ? (
        <>
          <FormControl>
            <FormLabel fontSize="md" fontWeight="bold">
              Ingresa tu correo electrónico
            </FormLabel>

            <Text mb={2} fontSize="sm" color="gray.600" userSelect="none">
              Te enviaremos un enlace para que puedas restablecer tu contraseña.
              Revisa tu bandeja de entrada y sigue los pasos a continuación.
            </Text>

            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faUser} />
              </InputLeftElement>
              <Input
                fontSize="sm"
                type="email"
                placeholder="ejemplo@correo.com"
                borderWidth="2px"
                borderColor={recoveryError ? "red.300" : "whiteAlpha.400"}
                onFocus={() => setRecoveryError("")}
                value={recoverEmail}
                onChange={(e) => setRecoverEmail(e.target.value)}
                _focus={{ borderColor: "brand.400" }}
              />
            </InputGroup>
          </FormControl>

          <Button
            mt={4}
            bg="brand.500"
            color="brand.900"
            size="lg"
            borderRadius="2xl"
            w="100%"
            onClick={handlePasswordReset}
            isLoading={isLoading}
            loadingText="Enviando..."
          >
            Enviar enlace de recuperación
          </Button>

          {recoverySuccess && (
            <MotionAlert variant="glass"
              key="recovery-success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              fontSize="sm"
              my={2}
              status="success"
              borderRadius={12}
            >
              <AlertIcon />
              {"Se ha enviado un enlace de recuperación a " +
                recoverEmail}
              <CloseButton
                size="sm"
                position="absolute"
                right="2px"
                top="8px"
                onClick={() => {
                  setIsRecovering(false);
                  setRecoveryError("");
                  setRecoverEmail("");
                }}
              />
            </MotionAlert>
          )}

          {recoveryError && (
            <MotionAlert variant="glass"
              key="recovery-error"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              fontSize="sm"
              my={2}
              status="error"
              borderRadius={12}
            >
              <AlertIcon />
              {recoveryError}
              <CloseButton
                size="sm"
                position="absolute"
                right="2px"
                top="8px"
                onClick={() => setRecoveryError("")}
              />
            </MotionAlert>
          )}

          <Text fontSize="sm" textAlign="center" mt={2}>
            ¿Ya recordaste tu contraseña?{" "}
            <Link
              fontWeight="bold"
              color="brand.800"
              textDecoration="underline"
              onClick={() => {
                setIsRecovering(false);
                setRecoveryError("");
                setRecoverEmail("");
                setRecoverySuccess(false);
              }}
            >
              Volver a iniciar sesión
            </Link>
          </Text>
        </>
      ) : (
        <>
          {/* FORMULARIO DE LOGIN */}
          <FormControl>
            <FormLabel fontSize="md" fontWeight="bold">
              Nombre de usuario o correo electrónico
            </FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faUser} />
              </InputLeftElement>
              <Input
                fontSize="sm"
                type="text"
                placeholder="Ej. Juan123 o juan123@gmail.com"
                borderWidth="2px"
                borderColor={error ? "red.300" : "whiteAlpha.400"}
                onFocus={() => setError("")}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                _focus={{ borderColor: "brand.400" }}
              />
            </InputGroup>
          </FormControl>

          <FormControl>
            <FormLabel fontWeight="bold" fontSize="md">
              Contraseña
            </FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faLock} />
              </InputLeftElement>
              <Input
                fontSize="sm"
                type={showPassword ? "text" : "password"}
                placeholder="Ingresa tu contraseña"
                borderWidth="2px"
                borderColor={error ? "red.300" : "whiteAlpha.400"}
                onFocus={() => setError("")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                _focus={{ borderColor: "brand.400" }}
              />
              <InputRightElement>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </Button>
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <Button
            bg="brand.500"
            color="brand.900"
            size="lg"
            borderRadius="2xl"
            w="100%"
            onClick={handleLogin}
            isLoading={isLoading}
            loadingText="Iniciando sesión..."
          >
            Iniciar sesión
          </Button>

          {error && (
            <MotionAlert variant="glass"
              key="alert"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 10,
                duration: 0.4,
              }}
              fontSize="sm"
              my={2}
              status="error"
              borderRadius={12}
            >
              <AlertIcon />
              {error} {userNotFound && " "}
              {userNotFound && (
                <Link
                  onClick={handleRegister}
                  fontWeight="bold"
                  color="brand.800"
                  textDecoration="underline"
                >
                  ¡Regístrate!
                </Link>
              )}
              <CloseButton
                size="sm"
                position="absolute"
                right="2px"
                top="8px"
                onClick={() => setError("")}
              />
            </MotionAlert>
          )}

          <Text fontSize="sm" textAlign="center">
            ¿Olvidaste tu contraseña?{" "}
            <Link
              fontWeight="bold"
              color="brand.800"
              textDecoration="underline"
              onClick={() => setIsRecovering(true)}
            >
              Recupérala
            </Link>
          </Text>

          <Text
            fontSize="sm"
            textAlign="center"
            display={{ base: "block", lg: "none" }}
          >
            ¿No tienes una cuenta Codex?{" "}
            <Link
              onClick={handleRegister}
              fontWeight="bold"
              color="brand.800"
              isExternal
            >
              ¡Regístrate ahora!
            </Link>
          </Text>
        </>
      )}
    </MotionVStack>
  );
};

export default LoginForm;
